/**
 * Discovery-UI:
 *  - RecentlyViewedStrip: horizontaler Strip am Ende der Page mit den
 *    zuletzt angesehenen Produkten (localStorage).
 *  - CrossSellsBlock: "Ähnliche Produkte" auf der PDP (Backend-driven).
 *  - TrustBadgesBar: Versand-/Sicherheit-Pills im Hero.
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Lock, ShieldCheck, Truck, Zap } from "lucide-react";
import { getRelatedProducts, type PublicProduct } from "../lib/page";
import { useRecentViewed } from "../lib/discovery_store";
import { navigate } from "../lib/router";

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
  bg: string;
};

// ============================================================
// Trust-Badges-Bar (im Hero)
// ============================================================

export function TrustBadgesBar({
  theme,
  isDark,
  hasWhatsApp,
}: {
  theme: ThemeLike;
  isDark: boolean;
  hasWhatsApp: boolean;
}) {
  const badges = [
    { icon: <Truck className="size-3.5" />, label: "Versand 24h" },
    { icon: <ShieldCheck className="size-3.5" />, label: "Sichere Zahlung" },
    { icon: <Zap className="size-3.5" />, label: "B2B-Preise" },
  ];
  if (hasWhatsApp) {
    badges.push({ icon: <span>💬</span>, label: "Direkt-Kontakt" });
  }
  return (
    <div className="mt-6 flex flex-wrap gap-1.5 justify-center">
      {badges.map((b) => (
        <span
          key={b.label}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium border"
          style={{
            color: theme.inkMuted,
            background: theme.cardBg,
            borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
          }}
        >
          {b.icon}
          {b.label}
        </span>
      ))}
    </div>
  );
}

// ============================================================
// Cross-Sells (auf PDP, basierend auf Backend "related")
// ============================================================

export function CrossSellsBlock({
  agentCode,
  productId,
  theme,
  isDark,
  isB2bLocked,
}: {
  agentCode: string;
  productId: number;
  theme: ThemeLike;
  isDark: boolean;
  isB2bLocked: boolean;
}) {
  const { data } = useQuery({
    queryKey: ["related", agentCode, productId],
    queryFn: () => getRelatedProducts(agentCode, productId, 6),
    staleTime: 5 * 60_000,
  });
  const items = data?.items || [];
  if (items.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="text-lg md:text-xl font-bold mb-4" style={{ color: theme.ink }}>
        Auch interessant
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {items.map((p) => (
          <MiniProductCard
            key={p.id}
            product={p}
            theme={theme}
            isDark={isDark}
            isB2bLocked={isB2bLocked}
          />
        ))}
      </div>
    </section>
  );
}

// ============================================================
// Recently-Viewed Strip
// ============================================================

export function RecentlyViewedStrip({
  agentCode,
  excludeId,
  theme,
  isDark,
}: {
  agentCode: string;
  excludeId?: number | null;
  theme: ThemeLike;
  isDark: boolean;
}) {
  const items = useRecentViewed(agentCode, excludeId ?? null);
  if (items.length === 0) return null;
  return (
    <section className="mx-auto max-w-6xl px-4 mt-10">
      <h2 className="text-base md:text-lg font-bold mb-3" style={{ color: theme.ink }}>
        Zuletzt angesehen
      </h2>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {items.map((it) => (
          <a
            key={it.wc_product_id}
            href={`/p/${it.wc_product_id}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate(`/p/${it.wc_product_id}`);
            }}
            className="flex-shrink-0 w-32 md:w-40 rounded-xl overflow-hidden border transition hover:-translate-y-0.5"
            style={{
              background: theme.cardBg,
              borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
            }}
          >
            <div
              className="aspect-square overflow-hidden"
              style={{ background: isDark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.03)" }}
            >
              {it.image_url ? (
                <img src={it.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : null}
            </div>
            <div className="p-2">
              <div className="text-xs font-medium leading-tight line-clamp-2 min-h-[2rem]" style={{ color: theme.ink }}>
                {it.name}
              </div>
              {it.price ? (
                <div className="text-sm font-bold mt-1" style={{ color: theme.accent }}>
                  {it.price} €
                </div>
              ) : null}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// Mini-ProductCard (verwendet in CrossSells + ggf. anderen Strips)
// ============================================================

function MiniProductCard({
  product: p,
  theme,
  isDark,
  isB2bLocked,
}: {
  product: PublicProduct;
  theme: ThemeLike;
  isDark: boolean;
  isB2bLocked: boolean;
}) {
  const pdpHref = !isB2bLocked && p.wc_product_id ? `/p/${p.wc_product_id}` : p.permalink;
  const opensExternal = !pdpHref.startsWith("/");
  return (
    <a
      href={pdpHref}
      target={opensExternal ? "_blank" : undefined}
      rel={opensExternal ? "noopener noreferrer" : undefined}
      onClick={(e) => {
        if (opensExternal) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(pdpHref);
      }}
      className="flex flex-col rounded-xl overflow-hidden border transition group hover:-translate-y-0.5"
      style={{
        background: theme.cardBg,
        borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
      }}
    >
      <div
        className="aspect-square overflow-hidden"
        style={{ background: isDark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.03)" }}
      >
        {p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : null}
      </div>
      <div className="p-2.5">
        <div className="text-xs md:text-sm font-medium leading-tight line-clamp-2 min-h-[2.25rem]" style={{ color: theme.ink }}>
          {p.name}
        </div>
        <div className="mt-1.5 flex items-end justify-between gap-1">
          {p.price ? (
            <div className="text-sm md:text-base font-bold" style={{ color: theme.accent }}>
              {p.price} €
            </div>
          ) : (
            <div className="text-[10px] font-semibold inline-flex items-center gap-1" style={{ color: theme.accent }}>
              <Lock className="size-3" />
              Login
            </div>
          )}
          {opensExternal && (
            <ExternalLink className="size-3 opacity-50" style={{ color: theme.inkMuted }} />
          )}
        </div>
      </div>
    </a>
  );
}
